import { observer } from 'mobx-react';
import React, { RefObject, forwardRef, useEffect, useRef, useState } from 'react';

import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import Slider from '@material-ui/core/Slider';
import { Theme, makeStyles } from '@material-ui/core/styles';

import { AudioExampleModel, AudioFileModel } from '../../shared/models/AudioTestModel';

function shouldUseNativeWebKitTransition() {
  const userAgent = navigator.userAgent;
  // All iPhone/iPad browsers use WebKit, even when branded Chrome or Firefox.
  const isAppleMobile = /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isDesktopSafari = /Safari/.test(userAgent) &&
    !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android/.test(userAgent);
  return isAppleMobile || isDesktopSafari;
}

/**
 * In order to build a custom audio player including rating bar,
 *  use this hook with AudioButton and AudioController components
 */
export function useAudioPlayer(audios: AudioFileModel[], sample: AudioFileModel, example: AudioExampleModel,
                               markExamplePlayedOnce = true) {
  // Keep Chromium's existing gain-node crossfade unchanged. WebKit uses a
  // native media-element hand-off to avoid its audible Web Audio gap.
  const useWebAudioCrossfade = !shouldUseNativeWebKitTransition();
  const crossfadeDuration = 128;
  // `timeupdate` is not frame-accurate, so leave a small margin to seek before
  // the browser emits `ended`. This keeps repeat playback within the original
  // participant gesture (particularly important on iOS).
  const loopEndMargin = 0.05;
  const [currentTime, setCurrentTime] = useState(0);
  const [refs] = useState(audios.map(() => React.createRef<HTMLAudioElement>()));
  const sampleRef = useRef<HTMLAudioElement>();
  const [activeRef, setActiveRef] = useState<RefObject<HTMLAudioElement>>(refs[0]);
  const [loopRange, setLoopRange] = useState<number[]>();
  // played times records how many times the audio played #
  const playedTimesRef = useRef(0);
  const lastPlaybackTimeRef = useRef(0);
  const [prevPlayedAudio, setPrevPlayedAudio] = useState<AudioFileModel>();
  const transitionId = useRef(0);
  const crossfadeTimer = useRef<number>();
  const nativeFadeFrame = useRef<number>();
  const audioContext = useRef<AudioContext>();
  const audioGains = useRef(new Map<HTMLAudioElement, GainNode>());

  useEffect(() => () => {
    if (crossfadeTimer.current) clearTimeout(crossfadeTimer.current);
    if (nativeFadeFrame.current) cancelAnimationFrame(nativeFadeFrame.current);
    audioContext.current?.close();
  }, []);

  const gainFor = (audio: HTMLAudioElement) => {
    const existingGain = audioGains.current.get(audio);
    if (existingGain) return existingGain;

    const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextConstructor) return undefined;

    const context = audioContext.current || new AudioContextConstructor();
    audioContext.current = context;
    context.resume().catch(() => {});
    const gain = context.createGain();
    context.createMediaElementSource(audio).connect(gain).connect(context.destination);
    audioGains.current.set(audio, gain);
    return gain;
  };

  // Include the reference audio when looking up the currently active clip.
  const includeAll = (): { allAudio: AudioFileModel[]; allRefs: RefObject<HTMLAudioElement>[] } =>
    ({
      allAudio: sample ? [...audios, sample] : audios,
      allRefs: sample ? [...refs, sampleRef] : refs,
    });

  const handlePlay = (current: AudioFileModel, startTime?: number) => {
    const thisTransition = ++transitionId.current;
    if (crossfadeTimer.current) clearTimeout(crossfadeTimer.current);
    if (nativeFadeFrame.current) cancelAnimationFrame(nativeFadeFrame.current);
    setPrevPlayedAudio(current);
    const { allAudio, allRefs } = includeAll();
    const previousIndex = allAudio.findIndex((audio) => audio.isActive);
    const currentIndex = allAudio.indexOf(current);
    const previousRef = previousIndex >= 0 ? allRefs[previousIndex]?.current : null;
    const currentRef = currentIndex >= 0 ? allRefs[currentIndex]?.current : null;
    const shouldCrossfade = !!previousRef && previousRef !== currentRef;

    if (!currentRef) return;

    // Let the media element handle a whole-track repeat. Unlike a timeupdate
    // callback, native looping cannot lose a race with `ended` at the exact
    // duration boundary (notably on iOS).
    currentRef.loop = (!loopRange || !Number.isFinite(currentRef.duration) ||
      loopRange[1] >= currentRef.duration - loopEndMargin) &&
      (!example.settings?.loopTimes || playedTimesRef.current + 1 < example.settings.loopTimes);

    const currentGain = useWebAudioCrossfade ? gainFor(currentRef) : undefined;
    const previousGain = useWebAudioCrossfade && shouldCrossfade && previousRef ? gainFor(previousRef) : undefined;

    // Keep the clip the participant is hearing untouched. Only the incoming
    // clip is positioned to match it before the audible switch happens.
    const requestedTime = startTime ?? previousRef?.currentTime ?? currentTime;
    // WebKit's native media hand-off reaches audible output slightly after its
    // reported media time. Compensate only normal synchronized switches; an
    // explicit startTime (for example, "always start from zero") stays exact.
    const compensatedTime = requestedTime + (!useWebAudioCrossfade && shouldCrossfade && startTime === undefined ? 0.064 : 0);
    const targetTime = loopRange && (compensatedTime < loopRange[0] || compensatedTime > loopRange[1])
      ? loopRange[0]
      : compensatedTime;
    if (previousRef !== currentRef) {
      try {
        currentRef.currentTime = targetTime;
      } catch (_) {
        // The browser will seek once metadata is available.
      }
    }

    allAudio.forEach((a, i: number) => {
      // Keep only the outgoing clip alive long enough for the crossfade.
      if (i !== currentIndex && allRefs[i]?.current !== previousRef) allRefs[i]?.current?.pause();
      allAudio[i].isActive = a === current;
      const audio = allRefs[i]?.current;
      const gain = useWebAudioCrossfade && audio ? audioGains.current.get(audio) : undefined;
      if (audio) {
        audio.muted = false;
        // Safari/WebKit uses the element's own volume for a native fade;
        // Chromium continues to receive the unchanged gain-node setup.
        audio.volume = !useWebAudioCrossfade && audio === currentRef && shouldCrossfade ? 0 : 1;
      }
      if (gain && audio !== currentRef && audio !== previousRef) {
        gain.gain.cancelScheduledValues(audioContext.current!.currentTime);
        gain.gain.setValueAtTime(0, audioContext.current!.currentTime);
      }
    });

    if (currentGain) {
      currentGain.gain.cancelScheduledValues(audioContext.current!.currentTime);
      currentGain.gain.setValueAtTime(shouldCrossfade ? 0 : 1, audioContext.current!.currentTime);
    }
    if (previousGain) {
      previousGain.gain.cancelScheduledValues(audioContext.current!.currentTime);
      previousGain.gain.setValueAtTime(1, audioContext.current!.currentTime);
    }

    setActiveRef(allRefs[currentIndex]);
    setCurrentTime(currentRef.currentTime);
    lastPlaybackTimeRef.current = currentRef.currentTime;
    // This is called directly from the participant's button click, which is
    // required by browsers that restrict media playback, including iOS Safari.
    currentRef.play().then(() => {
      if (thisTransition !== transitionId.current || !shouldCrossfade) return;

      if (!useWebAudioCrossfade) {
        const fadeStartedAt = performance.now();
        const fadeNatively = (now: number) => {
          if (thisTransition !== transitionId.current) return;
          const progress = Math.min(1, (now - fadeStartedAt) / crossfadeDuration);
          currentRef.volume = progress;
          previousRef.volume = 1 - progress;
          if (progress < 1) nativeFadeFrame.current = requestAnimationFrame(fadeNatively);
          else {
            currentRef.volume = 1;
            previousRef.volume = 1;
            previousRef.pause();
          }
        };
        nativeFadeFrame.current = requestAnimationFrame(fadeNatively);
        return;
      }

      // The outgoing clip has continued to advance while the incoming one was
      // being started muted. Align them again immediately before the fade.
      const latestTime = previousRef.currentTime;
      const synchronizedTime = loopRange && (latestTime < loopRange[0] || latestTime > loopRange[1])
        ? loopRange[0]
        : latestTime;
      try {
        currentRef.currentTime = synchronizedTime;
        setCurrentTime(synchronizedTime);
      } catch (_) {
        // The fade can still proceed if the browser has not made the clip seekable yet.
      }

      if (!currentGain || !previousGain) return;
      const fadeStart = audioContext.current!.currentTime;
      const fadeEnd = fadeStart + crossfadeDuration / 1000;
      currentGain.gain.setValueAtTime(0, fadeStart);
      currentGain.gain.linearRampToValueAtTime(1, fadeEnd);
      previousGain.gain.setValueAtTime(1, fadeStart);
      previousGain.gain.linearRampToValueAtTime(0, fadeEnd);
      crossfadeTimer.current = window.setTimeout(() => {
        if (thisTransition === transitionId.current) previousRef.pause();
      }, crossfadeDuration);
    }).catch(() => {});
  };

  const handlePause = () => {
    ++transitionId.current;
    if (crossfadeTimer.current) clearTimeout(crossfadeTimer.current);
    if (nativeFadeFrame.current) cancelAnimationFrame(nativeFadeFrame.current);
    // Deconstruction for all including reference audio
    const { allAudio, allRefs } = includeAll();
    allRefs.forEach((_, i: number) => {
      allAudio[i].isActive = false;
      allRefs[i].current?.pause();
      // State that if it is ready
      // console.log(allRefs[i].current.readyState)
    });
  };
  const handleTimeUpdate = () => {
    const activeAudio = activeRef?.current;
    if (!activeAudio) return;

    const range = loopRange || [0, activeAudio.duration];
    const loopStart = range[0];
    const loopEnd = range[1];
    const isWholeTrack = !loopRange || loopEnd >= activeAudio.duration - loopEndMargin;
    if (isWholeTrack && activeAudio.currentTime + loopEndMargin < lastPlaybackTimeRef.current) {
      // Native looping has just wrapped around; count the completed pass before
      // deciding whether the next pass should be the last one.
      if (markExamplePlayedOnce) example.playedOnce = true;
      playedTimesRef.current += 1;
    }
    lastPlaybackTimeRef.current = activeAudio.currentTime;
    const shouldRepeat = !example.settings?.loopTimes ||
      playedTimesRef.current + 1 < example.settings.loopTimes;
    const isWholeTrackLoop = isWholeTrack && shouldRepeat;

    // Switch native looping off as soon as the participant selects a section.
    // For the full track, the browser performs the exact-boundary repeat.
    activeAudio.loop = isWholeTrackLoop;
    if (isWholeTrackLoop) {
      setCurrentTime(activeAudio.currentTime);
      return;
    }

    // Seek while the element is still playing rather than calling play() from
    // `ended`. The original user gesture therefore remains the only start.
    if (Number.isFinite(loopEnd) && activeAudio.currentTime >= loopEnd - loopEndMargin && shouldRepeat) {
      activeAudio.currentTime = loopStart;
      playedTimesRef.current += 1;
      setCurrentTime(loopStart);
      return;
    }

    setCurrentTime(activeAudio.currentTime);
  };
  // This is now a fallback only: normal repeats are handled just before the
  // active element reaches its end, without a second play() call.
  const handleEnded = () => {
    if (markExamplePlayedOnce) example.playedOnce = true;
    playedTimesRef.current += 1;
    handlePause(); // Make sure the button style looks right
    // A later manual replay must start from the loop start, not the ended
    // timestamp that would otherwise be used for clip synchronization.
    resetCurrentTime();
  };
  // Reset the current playback position. Future clips are aligned only when
  // they become active, so they are never needlessly changed in the background.
  const resetCurrentTime = () => {
    const time = loopRange ? loopRange[0] : 0;
    setCurrentTime(time);
    if (activeRef?.current) activeRef.current.currentTime = time;
    lastPlaybackTimeRef.current = time;
  };

  const handleSeek = (_: any, newValue: number | number[]) => {
    const requestedTime = newValue ? Number(newValue) : 0;
    const time = loopRange && (requestedTime < loopRange[0] || requestedTime > loopRange[1])
      ? loopRange[0]
      : requestedTime;
    if (activeRef?.current) activeRef.current.currentTime = time;
    setCurrentTime(time);
    lastPlaybackTimeRef.current = time;
  };

  // // Reference audio
  // const sampleNode = <AudioButton audio={sample} ref={sampleRef} onPlay={handlePlay} onPause={handlePause}>Ref</AudioButton>
  //
  // const audioNodes = audios.map((v, i) =>
  //   <AudioButton key={i} audio={v} ref={refs[i]} onPlay={handlePlay} onPause={handlePause}
  //                onTimeUpdate={i === 0 ? handleTimeUpdate : undefined}>{i + 1}</AudioButton>
  // );
  // const controllerNode = <AudioController refs={refs} sampleRef={sampleRef} currentTime={currentTime}/>

  return {
    refs,
    sampleRef,
    activeRef,
    loopRange,
    setLoopRange,
    currentTime,
    handlePlay,
    handlePause,
    handleTimeUpdate,
    handleEnded,
    resetCurrentTime,
    handleSeek,
    prevPlayedAudio,
  };
}

/** This component exposes the audio to outside. Control audio with ref attribute. */
export const AudioButton = observer(
  forwardRef<
    HTMLAudioElement,
    {
      audio: AudioFileModel;
      onPlay: (v: AudioFileModel) => void;
      onPause: () => void;
      onTimeUpdate?: () => void;
      onEnded?: () => void;
      load?: boolean;
      source?: string;
      children?: any;
    }
  >(function (props, ref) {
    const { audio, onTimeUpdate, onPlay, onPause, onEnded, load = true, source } = props;

    // An AudioButton contains an audio element and a button. Use loop attribute, onEnded Event will not trigger.
    const icon = <Icon>{audio.isActive ? 'pause' : 'play_arrow'}</Icon>;
    return (
      <>
        <audio
          preload={load ? "auto" : "none"}
          // Sources are Blob URLs created only after the active question has
          // downloaded every clip in full. Inactive questions receive no src,
          // allowing their Blob memory to be released.
          src={source}
          controls
          ref={ref}
          style={{ display: 'none' }}
          onTimeUpdate={() => audio.isActive && onTimeUpdate?.()}
          onEnded={() => audio.isActive && onEnded?.()}
        />

        <Button
          variant={audio.isActive ? 'contained' : 'outlined'}
          color="primary"
          size="large"
          style={{ transition: 'none' }}
          // disabled={playedTimes >= settings?.loopTimes}
          startIcon={props.children && icon}
          onClick={() => (audio.isActive ? onPause() : onPlay(audio))}
        >
          {props.children ? props.children : icon}
        </Button>
      </>
    );
  })
);

const useStyles = makeStyles((_: Theme) => ({ thumb: { zIndex: 1 } }));

export function AudioController(props: {
  activeRef: RefObject<HTMLAudioElement>;
  currentTime: number;
  disabled?: boolean;
  onSeek: (event: any, newValue: number | number[]) => void;
}) {
  const { activeRef, currentTime, disabled, onSeek } = props;
  const classes = useStyles();

  const handleSliderLabelFormat = (num: number) => {
    return isNaN(num) ? 0 : num.toFixed(0) + 's';
  };

  if (!activeRef) return null;

  return (
    <Slider
      aria-labelledby="continuous-slider"
      defaultValue={0}
      step={0.1}
      min={0}
      classes={classes}
      max={activeRef.current?.duration}
      value={currentTime}
      onChange={onSeek}
      valueLabelDisplay="auto"
      valueLabelFormat={handleSliderLabelFormat}
      disabled={disabled}
    />
  );
}
